import type React from 'react';

import { Alert, Button, Text } from '@change/ds-core-ui';

import { usePageBanner } from './hooks/usePageBanner';
import type { Action, ActionButton, ButtonOnClick } from './types';

export const PageBanner: React.FC = () => {
	const { data, actions } = usePageBanner();

	if (!data.banner) return null;

	return (
		<Alert
			className="items-center justify-around pb-10 md:px-6 md:pb-0 xl:px-6 sm:[&>button]:flex sm:[&>div>button]:self-baseline! lg:[&>div>button]:self-auto! [&>div>div]:flex-row lg:[&>div>div]:items-center"
			type="page"
			containerClassName="relative md:static justify-between w-full items-center md:px-0 lg:[&>div>div]:w-full lg:[&>div>div>span]:w-full xl:[&>div>div>span]:w-200 lg:[&>div>div>span]:flex-row lg:[&>div>div>span]:flex lg:[&>div>div>span]:justify-between"
			variant="success"
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
			onDismissClick={(e) => actions.onDismiss(e as unknown as React.MouseEvent<HTMLButtonElement>)}
			dismissButtonAriaLabel="Close Alert"
		>
			<div className="mb-3 flex max-w-full flex-col lg:mb-0 lg:w-fit lg:flex-col lg:justify-between lg:self-center">
				<Text as="span" size="large" fontWeight="bold" className="text-typography-success">
					{data.banner.message}
				</Text>
			</div>
			<div className="absolute left-0 flex w-full flex-col px-4 pt-0 pb-6 md:static md:left-auto md:w-fit md:flex-col md:px-0 md:py-0 lg:justify-self-end">
				{data.banner.primaryAction && <PrimaryActionComponent {...data.banner.primaryAction} />}
			</div>
		</Alert>
	);
};

const PrimaryActionComponent: React.FC<Action> = (props) => {
	const testIds = { 'data-testid': 'page-message-primary-action', 'data-qa': 'page-message-primary-action' };

	const adaptHandler = (callbackFn: ActionButton['onClick']): ButtonOnClick | undefined => {
		if (!callbackFn) return undefined;
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		return callbackFn as ButtonOnClick;
	};

	// eslint-disable-next-line react/destructuring-assignment
	if (props.type === 'button') {
		const { type, text, onClick, ...buttonProps } = props;
		return (
			// eslint-disable-next-line @change-corgi/require-data-qa-attribute
			<Button
				{...testIds}
				{...buttonProps}
				onClick={adaptHandler(onClick)}
				variant="secondary"
				size="small"
				className="w-full md:w-auto lg:mr-4"
			>
				{text}
			</Button>
		);
	}

	const { type, text, to, onClick, ...linkProps } = props;
	return (
		// eslint-disable-next-line @change-corgi/require-data-qa-attribute
		<Button
			{...testIds}
			{...linkProps}
			as="a"
			href={typeof to === 'string' ? to : to.pathname || '#'}
			onClick={adaptHandler(onClick)}
			variant="secondary-solid"
			size="x-small"
			className="w-full md:w-auto lg:mr-4"
		>
			{text}
		</Button>
	);
};
